import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  PenTool, ArrowRight, BookOpen, Users, BarChart3, Shield,
  GraduationCap, School, Check, Minus, LayoutDashboard, LogOut, ChevronDown,
} from 'lucide-react';

export default function Index() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserEmail(session?.user?.email ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserEmail(session?.user?.email ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (document.querySelector('script[src*="storylane"]')) return;
    const script = document.createElement('script');
    script.src = 'https://js.storylane.io/js/v2/storylane.js';
    script.async = true;
    script.setAttribute('data-verify-origin', '');
    document.head.appendChild(script);
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUserEmail(null);
  };

  const initials = userEmail ? userEmail[0].toUpperCase() : '?';

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Writing Assessment and Moderation for Primary Schools"
        description="WriteAssess helps primary teachers assess pupil writing, check the evidence, and secure moderation-ready judgements with less workload."
        canonical="/"
      />
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <PenTool className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-xl">WriteAssess</span>
          </button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="hidden sm:inline-flex" onClick={() => navigate('/pricing')}>
              Pricing
            </Button>
            <Button variant="ghost" className="hidden sm:inline-flex" asChild>
              <a href="/blog">Blog</a>
            </Button>
            <Button variant="ghost" className="hidden sm:inline-flex" onClick={() => navigate('/help')}>
              Help
            </Button>
            {userEmail ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm hidden sm:inline">Welcome back</span>
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Go to Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/auth')}>
                  Sign in
                </Button>
                <Button onClick={() => navigate('/auth?plan=individual')}>
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-12 pb-8 sm:pt-20 sm:pb-12">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <BookOpen className="h-4 w-4" />
              Built for Primary Schools in England
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Photograph pupil writing. Get a WTS, EXS or GDS judgement you can check.
            </h1>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              WriteAssess reads handwritten work, compares it with official DfE exemplification materials, and suggests a best-fit judgement with the reasons behind it. Ask it why, override it, or accept it. You make the final call.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button size="lg" onClick={() => navigate('/auth?plan=individual')} className="text-lg h-12 px-8">
                <GraduationCap className="mr-2 h-5 w-5" />
                Start free as a teacher
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/auth?plan=school')} className="text-lg h-12 px-8">
                <School className="mr-2 h-5 w-5" />
                Set up a school account
              </Button>
            </div>
            <p className="text-sm">
              <Link to="/try" className="text-primary font-medium hover:underline">
                Or try one free assessment, no account needed
              </Link>
            </p>
            <p className="text-sm text-muted-foreground pt-2">
              Free to start. No card, no demo call.
            </p>
          </div>
        </div>
      </section>

      {/* Demo */}
      <section className="pt-10 pb-16 sm:pt-12 sm:pb-20 bg-muted/30 border-t border-b">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-4">See evidence-led assessment in action</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Upload a pupil's writing sample, review the suggested judgement, and see the evidence behind it.
            </p>
          </div>
          <div className="max-w-[960px] mx-auto">
            <div
              className="sl-embed"
              style={{
                position: 'relative',
                paddingBottom: 'calc(48.67% + 25px)',
                width: '100%',
                height: 0,
                transform: 'scale(1)',
              }}
            >
              <iframe
                loading="lazy"
                className="sl-demo"
                src="https://demo.storylane.com/demo/i1mvdzdkkezx?embed=popup"
                name="sl-embed"
                allow="fullscreen"
                allowFullScreen
                title="WriteAssess product demo"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: '1px solid rgba(63,95,172,0.35)',
                  boxShadow: '0px 0px 18px rgba(26, 19, 72, 0.15)',
                  borderRadius: '10px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Built around teacher judgement</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Turn pupil writing into clearer evidence, consistent records, and moderation-ready assessments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card rounded-2xl p-6 shadow-soft">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <PenTool className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Suggested judgements</h3>
              <p className="text-muted-foreground text-sm">
                Receive a suggested judgement against the writing framework, with evidence teachers can check before making the final decision.
              </p>
            </div>

            <div className="bg-card rounded-2xl p-6 shadow-soft">
              <div className="h-12 w-12 rounded-xl bg-success/10 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-success" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Evidence over time</h3>
              <p className="text-muted-foreground text-sm">
                Keep assessment records for each pupil so teachers and leaders can see how writing judgements develop across the year.
              </p>
            </div>

            <div className="bg-card rounded-2xl p-6 shadow-soft">
              <div className="h-12 w-12 rounded-xl bg-info/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-info" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Shared school view</h3>
              <p className="text-muted-foreground text-sm">
                Teachers manage their own classes while school leaders see assessment patterns across classes, year groups, and key stages.
              </p>
            </div>

            <div className="bg-card rounded-2xl p-6 shadow-soft">
              <div className="h-12 w-12 rounded-xl bg-warning/10 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-warning-foreground" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Pupil data protected</h3>
              <p className="text-muted-foreground text-sm">
                Built-in anonymisation options and secure handling help protect pupil information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Plan teaser */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Plans for teachers, schools and trusts</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Start as an individual teacher, or choose a school plan when you need shared records and moderation tools across your team.
            </p>
          </div>

          <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6 mb-10">
            {/* Individual teaser */}
            <div className="bg-card rounded-2xl p-8 shadow-soft border-2 border-transparent hover:border-primary/30 transition-colors">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <GraduationCap className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-1">Individual Teacher</h3>
              <p className="text-muted-foreground text-sm mb-4">For teachers assessing their own pupils and building evidence for writing judgements.</p>
              <p className="text-3xl font-bold mb-1">
                Free
                <span className="text-base font-normal text-muted-foreground"> · or from £9/mo</span>
              </p>
              <p className="text-xs text-muted-foreground mb-6">Free plan available, no card required</p>
              <Button className="w-full mb-6" onClick={() => navigate('/auth?plan=individual')}>
                Get Started Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <ul className="space-y-2.5 text-sm">
                {[
                  'Your own classes and pupils',
                  'Full writing assessment workflow',
                  'Progress charts and draft report comments',
                  'CSV pupil import',
                  'Upload multi-page writing samples',
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-muted-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0" />{f}
                  </li>
                ))}
                {[
                  'Multi-teacher collaboration',
                  'School-wide assessment overview',
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-muted-foreground/40">
                    <Minus className="h-4 w-4 shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>

            {/* School teaser */}
            <div className="bg-card rounded-2xl p-8 shadow-soft border-2 border-primary/20 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">For schools</span>
              </div>
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <School className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-1">School Plan</h3>
              <p className="text-muted-foreground text-sm mb-4">For schools that want clearer, more consistent writing judgements across classes, year groups, and key stages.</p>
              <p className="text-3xl font-bold mb-1">
                From £49
                <span className="text-base font-normal text-muted-foreground">/mo</span>
              </p>
              <p className="text-xs text-muted-foreground mb-6">Starter, Standard, and Plus tiers — invoicing available</p>
              <Button variant="outline" className="w-full mb-6" onClick={() => navigate('/pricing')}>
                See School Plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <ul className="space-y-2.5 text-sm">
                {[
                  'Your own classes and pupils',
                  'Full writing assessment workflow',
                  'Progress charts and draft report comments',
                  'CSV pupil import',
                  'Upload multi-page writing samples',
                  'Multi-teacher collaboration',
                  'School-wide assessment overview',
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-muted-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-2 mb-8">
            Running a multi-academy trust?{' '}
            <button className="text-primary hover:underline font-medium" onClick={() => navigate('/contact')}>
              Contact us for custom trust-wide pricing →
            </button>
          </p>

          <div className="text-center">
            <Button variant="outline" size="lg" onClick={() => navigate('/pricing')}>
              See full pricing breakdown
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-primary rounded-3xl p-8 lg:p-12 text-center">
            <h2 className="text-3xl font-bold text-primary-foreground mb-4">
              Ready to make writing judgements easier to evidence?
            </h2>
            <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
              Give teachers time back while keeping professional judgement, clear evidence, and moderation at the centre.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                onClick={() => navigate('/auth?plan=individual')}
                className="text-lg h-12 px-8">
                <GraduationCap className="mr-2 h-5 w-5" />
                Start as a teacher
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/auth?plan=school')}
                className="text-lg h-12 px-8 bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10">
                <School className="mr-2 h-5 w-5" />
                Set up a school account
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                <PenTool className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-bold">WriteAssess</span>
            </button>
            <nav className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <button className="hover:text-foreground transition-colors" onClick={() => navigate('/pricing')}>Pricing</button>
              <a href="/blog" className="hover:text-foreground transition-colors">Blog</a>
              <button className="hover:text-foreground transition-colors" onClick={() => navigate('/auth')}>Sign in</button>
              <button className="hover:text-foreground transition-colors" onClick={() => navigate('/terms')}>Terms</button>
              <button className="hover:text-foreground transition-colors" onClick={() => navigate('/privacy')}>Privacy</button>
              <button className="hover:text-foreground transition-colors" onClick={() => navigate('/safeguarding')}>Safeguarding</button>
              <button className="hover:text-foreground transition-colors" onClick={() => navigate('/contact')}>Contact</button>
              <button className="hover:text-foreground transition-colors" onClick={() => navigate('/help')}>Help</button>
            </nav>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} WriteAssess. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
